/* Admin Center — generic Lookup Table editor (Project Types, Fee Structures,
   Billing Codes, Phases, Overhead Categories, Vacation Categories).
   Extracted from AdminPage.tsx. */

import { useState, useEffect } from 'react';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { GripVertical, Trash2 } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import { useTableSync } from '../../hooks/useTableSync';
import type { AdminPageProps } from './types';
import {
  inputStyle,
  btnPrimary,
  btnDanger,
  DRAG_HANDLE_STYLE,
  useDragReorder,
  RowSkeleton,
} from './shared';

/* ─── Simple Lookup Table Editor (Project Types, Fee Structures, Billing Codes) ─── */
function LookupTableTab({
  title,
  description,
  service,
  tableName,
  toast,
}: {
  title: string;
  description: string;
  service: {
    list: () => Promise<any[]>;
    save: (item: any) => Promise<any>;
    delete: (id: string) => Promise<void>;
  };
  /** Backing table name so we only refetch when the relevant table changed. */
  tableName: string;
  toast: AdminPageProps['toast'];
}) {
  const [items, setItems] = useState<any[]>([]);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);
  useTableSync([tableName], () => load());
  async function load() {
    try {
      const rows = await service.list();
      rows.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setItems(rows);
    } finally {
      setLoading(false);
    }
  }

  async function add() {
    if (!newName.trim()) return;
    try {
      await service.save({
        id: '',
        name: newName.trim(),
        sortOrder: items.length,
        createdAt: '',
        updatedAt: '',
      });
      setNewName('');
      await load();
      toast(`${title} added`);
    } catch (err: any) {
      toast('Save failed: ' + err.message, true);
    }
  }

  async function update(id: string) {
    if (!editingName.trim()) return;
    const item = items.find((i) => i.id === id);
    if (item) {
      try {
        await service.save({ ...item, name: editingName.trim() });
        setEditingId(null);
        await load();
        toast(`${title} updated`);
      } catch (err: any) {
        toast('Update failed: ' + err.message, true);
      }
    }
  }

  async function remove(id: string) {
    if (!confirm(`Delete this ${title.toLowerCase()}?`)) return;
    try {
      await service.delete(id);
      await load();
      toast(`${title} deleted`);
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  // Drag-to-reorder: rewrite sortOrder for every row whose index changed, then reload.
  const drag = useDragReorder(items, async (reordered) => {
    setItems(reordered); // Optimistic
    try {
      await Promise.all(
        reordered.map((it, i) => {
          if ((items.find((x) => x.id === it.id)?.sortOrder ?? -1) !== i) {
            return service.save({ ...it, sortOrder: i });
          }
          return Promise.resolve();
        })
      );
    } catch (err: any) {
      toast('Reorder failed: ' + err.message, true);
      await load(); // Rollback
    }
  });

  return (
    <div>
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {description}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder={`New ${title.toLowerCase()}...`}
          style={{ ...inputStyle, width: rem(300) }}
        />
        <button onClick={add} style={btnPrimary}>
          + Add
        </button>
      </div>

      <div
        style={{
          background: 'var(--sf)',
          borderRadius: rem(8),
          border: '1px solid var(--bd)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <RowSkeleton label={`Loading ${title.toLowerCase()}`} />
        ) : items.length === 0 ? (
          <EmptyState
            title={`No ${title.toLowerCase()} defined`}
            subtitle="Defaults will be seeded automatically."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map((item, i) => {
              const isDragging = drag.dragIdx === i;
              const isDropTarget =
                drag.overIdx === i && drag.dragIdx !== null && drag.dragIdx !== i;
              return (
                <div
                  key={item.id}
                  {...drag.rowProps(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderBottom: i < items.length - 1 ? '1px solid var(--bd)' : 'none',
                    borderTop:
                      isDropTarget && drag.dragIdx! > i ? '2px solid var(--ac)' : undefined,
                    background: isDragging
                      ? 'rgba(var(--acr),0.08)'
                      : isDropTarget
                        ? 'rgba(var(--acr),0.04)'
                        : undefined,
                    opacity: isDragging ? 0.5 : 1,
                    transition: 'background .1s',
                  }}
                >
                  <span
                    {...drag.handleProps()}
                    style={{ ...DRAG_HANDLE_STYLE, ...drag.handleProps().style }}
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <Icon icon={GripVertical} em={14 / 16} />
                  </span>
                  {editingId === item.id ? (
                    <>
                      <input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && update(item.id)}
                        style={{ ...inputStyle, flex: 1 }}
                        autoFocus
                      />
                      <button
                        onClick={() => update(item.id)}
                        style={{ ...btnPrimary, padding: `${rem(5)} ${rem(10)}` }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontSize: 'var(--font-size-base)' }}>
                        {item.name}
                      </span>
                      <button
                        onClick={() => {
                          setEditingId(item.id);
                          setEditingName(item.name);
                        }}
                        style={{ ...btnDanger, color: 'var(--tm)' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        style={btnDanger}
                        title={`Delete ${title.toLowerCase()}`}
                        aria-label={`Delete ${item.name}`}
                      >
                        <Icon icon={Trash2} em={14 / 12} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export { LookupTableTab };
